// Загрузка данных страницы: useApi(адрес, { key, query, watch, immediate, default }).
// Результат можно ждать (await) — страница отрисуется уже с данными; с key данные общие
// для всех мест (например, сеанс), refreshData(key) обновляет их везде.
import { isRef, ref, shallowRef, toValue, unref, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import { api, ApiError, type Query } from './http';

export type Status = 'idle' | 'pending' | 'success' | 'error';
export interface ApiState<T> {
  data: Ref<T | null>;
  error: Ref<ApiError | null>;
  pending: Ref<boolean>;
  status: Ref<Status>;
  refresh: () => Promise<void>;
  execute: () => Promise<void>;
  clear: () => void;
}
export interface UseApiOptions<T> {
  key?: string;
  query?: MaybeRefOrGetter<Query | undefined> | Query;
  watch?: false | unknown[];
  immediate?: boolean;
  default?: () => T;
  method?: string;
  body?: unknown;
}

interface Entry { state: ApiState<any>; inflight: Promise<void> | null; inflightKey: string; seq: number; loaders: Set<() => Promise<void>> }
const keyed = new Map<string, Entry>();

function makeState<T>(initial: T | null): ApiState<T> {
  return { data: shallowRef(initial) as Ref<T | null>, error: shallowRef(null), pending: ref(false), status: ref('idle'), refresh: async () => {}, execute: async () => {}, clear: () => {} };
}

export function useApi<T = any>(url: MaybeRefOrGetter<string>, options: UseApiOptions<T> = {}): ApiState<T> & Promise<ApiState<T>> {
  const entry: Entry = options.key && keyed.has(options.key)
    ? keyed.get(options.key)!
    : { state: makeState<T>(options.default ? options.default() : null), inflight: null, inflightKey: '', seq: 0, loaders: new Set() };
  if (options.key) keyed.set(options.key, entry);
  const state = entry.state as ApiState<T>;

  // Параметры запроса — отпечаток: новый набор данных (сменился месяц, фильтр) обязан
  // вытеснить прежний запрос, а устаревший ответ — не перезаписать более свежие данные.
  const fingerprint = () => JSON.stringify([toValue(url), unref(toValue(options.query as MaybeRefOrGetter<Query | undefined>)), options.method ?? 'GET', options.body]);
  const load = async () => {
    const ticket = ++entry.seq;
    state.pending.value = true;
    state.status.value = 'pending';
    try {
      const data = await api<T>(toValue(url), { query: unref(toValue(options.query as MaybeRefOrGetter<Query | undefined>)), method: options.method, body: options.body });
      if (ticket !== entry.seq) return;
      state.data.value = data;
      state.error.value = null;
      state.status.value = 'success';
    } catch (error) {
      if (ticket !== entry.seq) return;
      state.error.value = error instanceof ApiError ? error : new ApiError(0, { message: 'Нет связи с порталом.' });
      state.status.value = 'error';
    } finally {
      if (ticket === entry.seq) state.pending.value = false;
    }
  };
  const refresh = () => {
    const current = fingerprint();
    if (!entry.inflight || entry.inflightKey !== current) {
      entry.inflightKey = current;
      entry.inflight = load().finally(() => { if (entry.inflightKey === current) entry.inflight = null; });
    }
    return entry.inflight;
  };
  entry.loaders.add(refresh);
  state.refresh = state.execute = refresh;
  state.clear = () => { state.data.value = options.default ? options.default() : null; state.error.value = null; state.status.value = 'idle'; };

  if (options.watch !== false) {
    const sources: unknown[] = [() => toValue(url)];
    if (options.query !== undefined) sources.push(() => JSON.stringify(unref(toValue(options.query as MaybeRefOrGetter<Query | undefined>))));
    if (Array.isArray(options.watch)) sources.push(...options.watch.map(item => (isRef(item) || typeof item === 'function') ? item : () => item));
    watch(sources as any, () => { void refresh(); });
  }
  // Как прежний useFetch: при каждом открытии страницы данные запрашиваются заново (одновременные запросы по ключу — один).
  const started = options.immediate === false ? Promise.resolve() : refresh();
  const done = started.then(() => state);
  return Object.assign(done, state);
}

/** Обновить общие данные: по ключам или все. */
export async function refreshData(keys?: string | string[]): Promise<void> {
  const wanted = keys === undefined ? [...keyed.keys()] : Array.isArray(keys) ? keys : [keys];
  await Promise.all(wanted.map(key => keyed.get(key)?.state.refresh()));
}

/** Забыть общие данные (выход из портала). */
export function clearData(keys?: string | string[]): void {
  const wanted = keys === undefined ? [...keyed.keys()] : Array.isArray(keys) ? keys : [keys];
  for (const key of wanted) { keyed.get(key)?.state.clear(); keyed.delete(key); }
}
